import { service } from '@ember/service'
import Component from '@glimmer/component'
import { tracked, cached } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import { SearchBox } from 'teacher-ui-client/components/search-box'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import config from 'teacher-ui-client/config/environment'
import type { Log } from '@blakeelearning/log'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type Owner from '@ember/owner'
import { join } from 'teacher-ui-client/utils/uri'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import UiButtonLinkTo from 'teacher-ui-client/components/ui-button/link-to'
import { array } from '@ember/helper'
import { trackedFunction } from 'reactiveweb/function'
import RadioGroup from 'teacher-ui-client/components/base/radio-group'
import { eq } from 'ember-truth-helpers'
import type { BufferedChangeset } from 'ember-changeset/types'
import {
  validateFormat,
  validateLength,
  validateNumber,
  validatePresence,
} from 'ember-changeset-validations/validators'
import { Changeset } from 'ember-changeset'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import FormField from 'teacher-ui-client/components/form-field'

// Long Write is a precinct in Writing Legends

interface LongWrite {
  activity_id: string
  grade_positions: number[]
  precinct: string
  sort_published_date_desc: string
  sort_ranking_asc: number
  title: string
}

export interface LongWriteData {
  items: LongWrite[]
}

interface Signature {
  Args: {
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

const MINIMUM_TARGET_WORD_COUNT = 100
const MAXIMUM_TARGET_WORD_COUNT = 3000
const CREATE_YOUR_OWN_ACTIVITY_ID = 'c000longwritewriteyourown'
const ASSIGNMENT_TYPE_FROM_LIBRARY = 'from-library'
const ASSIGNMENT_TYPE_CUSTOM_TOPIC = 'custom-topic'
const MINIMUM_TOPIC_TITLE_LENGTH = 5

/**
 * A component that displays a list dropdown for Writing Legends' Long Writes
 */
export class ActivitySelectorLongWriteList extends Component<Signature> {
  @service log!: Log

  @service intl!: IntlService

  @tracked searchValue?: Nullable<string> = ''

  @tracked longWriteAssignmentType = ASSIGNMENT_TYPE_FROM_LIBRARY

  @tracked activityFromLibrary?: Nullable<Pick<LongWrite, 'activity_id' | 'title'>>

  @tracked assignmentChangeset!: BufferedChangeset

  @queryParam('assignments/product/create') writingCode?: string

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)

    const validations = {
      targetWordCount: [
        validateNumber({
          integer: true,
          gte: MINIMUM_TARGET_WORD_COUNT, // greater than or equal to
          lte: MAXIMUM_TARGET_WORD_COUNT, // less than or equal to
          message: this.intl.t('assignments.create.writingAssignments.longWrites.targetWordCountMessage', {
            min: MINIMUM_TARGET_WORD_COUNT,
            max: MAXIMUM_TARGET_WORD_COUNT,
          }),
        }),
      ],
      topicTitle: [
        validatePresence(true),
        validateLength({ min: MINIMUM_TOPIC_TITLE_LENGTH }),
        validateFormat({
          // Avoid special characters that are not valid for a title
          regex: /^[^<>]*$/,
        }),
      ],
    }

    this.assignmentChangeset = Changeset(
      { targetWordCount: MINIMUM_TARGET_WORD_COUNT, topicTitle: '' }, // initial model
      lookupValidator(validations, { intl: this.intl }),
      validations,
    )

    this.assignmentChangeset.on('afterValidation', () => {
      if (this.assignmentChangeset.isValid) {
        this.setAssignmentActivity()
      } else {
        this.selectLongWrite(null) // reset selected long write if validation fails
      }
    })
  }

  loadLongWrites = trackedFunction(this, async () => {
    const url = join(config.lessonsApiV1Url, 'writing_legends/long_writes').href
    const longWritesData = await this.fetchData<LongWriteData>(url)
    this.preSelectLongWrite(longWritesData)
    return longWritesData
  })

  async fetchData<R>(url: string): Promise<R | undefined> {
    try {
      const response = await fetch(url)

      if (response.ok) {
        return (await response.json()) as R
      } else {
        // Some unknown error. Log it.
        this.log.error(`Failed to fetch long writes list; Reason: (${response.status})`, url)
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to fetch long writes list; Reason: (error thrown)`, e ?? {}, url)
      }
    }
  }

  preSelectLongWrite = (longWritesData?: LongWriteData) => {
    // if Long Write code (activity_id) is provided in the query params, pre-select the Long Write
    if (!this.writingCode) return

    const preSelectedLongWrite = longWritesData?.items.find((longWrite) => longWrite.activity_id === this.writingCode)
    if (preSelectedLongWrite) {
      this.selectLongWrite(preSelectedLongWrite)
      this.searchValue = preSelectedLongWrite.title
    }
  }

  get filteredLongWrites() {
    return this.loadLongWrites.value?.items.filter((longWrite) =>
      longWrite.title.toLowerCase().includes(this.searchValue?.toLowerCase() ?? ''),
    )
  }

  // Sets the selected long write in the component, and sends the model to the `selectAssignmentAction`
  selectLongWrite = (longWrite: Nullable<Pick<LongWrite, 'activity_id' | 'title'>>) => {
    if (!longWrite) {
      this.args.selectAssignmentAction()
      return
    }
    this.args.selectAssignmentAction({
      id: longWrite.activity_id,
      label: longWrite.title,
      assignmentData: {
        assignment_id: longWrite.activity_id,
        assignment_type: 'long-write',
        wl_ewi_title: longWrite.title,
        wl_target_word_count: Number(this.assignmentChangeset.get('targetWordCount')),
      },
    })
  }

  selectLongWriteFromLibrary = (longWrite: Pick<LongWrite, 'activity_id' | 'title'>) => {
    this.activityFromLibrary = longWrite
    this.searchValue = longWrite.title

    this.assignmentChangeset.set('topicTitle', longWrite.title)
  }

  findLongWriteByTitle = (newSearchTerm: string | null | undefined) => {
    this.searchValue = newSearchTerm
    if (!newSearchTerm) this.selectLongWrite(null)
  }

  get selectedLongWrite() {
    const { selectedAssignmentOption } = this.args
    return selectedAssignmentOption?.assignmentData.assignment_id
  }

  @cached
  get longWriteAssignmentOptions() {
    return {
      id: 'lw-assignment-options',
      options: [
        {
          id: ASSIGNMENT_TYPE_FROM_LIBRARY,
          label: this.intl.t('assignments.create.writingAssignments.longWrites.selectFromLibrary'),
        },
        {
          id: ASSIGNMENT_TYPE_CUSTOM_TOPIC,
          label: this.intl.t('assignments.create.writingAssignments.longWrites.createCustomTopic'),
        },
      ],
    }
  }

  changeAssignmentType = (_optionSet: unknown, value: string) => {
    this.longWriteAssignmentType = value
    this.searchValue = '' // reset search value

    this.assignmentChangeset.set('topicTitle', '')
  }

  setAssignmentActivity = () => {
    if (this.longWriteAssignmentType === ASSIGNMENT_TYPE_CUSTOM_TOPIC) {
      this.selectLongWrite({
        activity_id: CREATE_YOUR_OWN_ACTIVITY_ID,
        title: this.assignmentChangeset.get('topicTitle'),
      })
    } else if (this.activityFromLibrary) {
      this.selectLongWrite(this.activityFromLibrary)
    }
  }

  resetSearch = () => {
    this.searchValue = ''
  }

  <template>
    <div class="mb-4 px-3 md:w-full lg:w-1/2 lg:min-w-fit">
      <label>{{t "assignments.create.writingAssignments.longWrites.selectOne"}}</label>
      <div>
        <RadioGroup
          @listClass="flex flex-col gap-1"
          @checkedOptionId={{this.longWriteAssignmentType}}
          @optionSet={{this.longWriteAssignmentOptions}}
          @onSelectionChange={{this.changeAssignmentType}}
        />
      </div>
      {{#if (eq this.longWriteAssignmentType ASSIGNMENT_TYPE_CUSTOM_TOPIC)}}
        <div class="mt-4">
          <FormField
            @changeset={{this.assignmentChangeset}}
            @propertyName="topicTitle"
            @labelText={{t "assignments.create.writingAssignments.longWrites.customTopic"}}
            as |field|
          >
            <field.label class="font-bold" />
            <field.input
              type="text"
              class="border-dusty-black-100 w-full rounded-sm border px-3 py-2"
              placeholder={{t "assignments.create.writingAssignments.longWrites.customTopicPlaceholder"}}
            />
          </FormField>
        </div>
      {{else}}
        <label class="mt-4 mb-1">
          {{t "assignments.create.writingAssignments.longWrites.title"}}
        </label>
        <div class="flex gap-4">
          {{#if this.loadLongWrites.value}}
            <SearchBox
              class="w-full"
              @value={{this.searchValue}}
              @placeholder={{t "assignments.create.writingAssignments.searchPlaceholder"}}
              @updateAction={{this.findLongWriteByTitle}}
              @selectOptionAction={{this.selectLongWriteFromLibrary}}
              @autocomplete={{true}}
              @autocompleteOptions={{this.filteredLongWrites}}
              as |longWrite|
            >
              <div class="space-y-3 font-normal">
                {{longWrite.title}}
              </div>
            </SearchBox>
            {{#if this.selectedLongWrite}}
              <UiButtonLinkTo
                @route="subscription-type.long-writes.previews"
                @models={{array this.selectedLongWrite}}
                class="whitespace-nowrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{t "assignments.create.writingAssignments.preview"}}<PhosphorIcon
                  @icon="phosphor-arrow-square-out-bold"
                  class="ml-2"
                />
              </UiButtonLinkTo>
            {{/if}}
          {{else}}
            <span class="text-dusty-black-200 italic">{{t
                "assignments.create.writingAssignments.longWrites.loadingMessage"
              }}</span>
          {{/if}}
        </div>
      {{/if}}

      <FormField
        @changeset={{this.assignmentChangeset}}
        @propertyName="targetWordCount"
        @labelText={{t "assignments.create.writingAssignments.longWrites.targetWordCount"}}
        as |field|
      >
        <field.label class="mt-2 font-bold" />
        <field.input
          type="number"
          min={{MINIMUM_TARGET_WORD_COUNT}}
          max={{MAXIMUM_TARGET_WORD_COUNT}}
          step="50"
          class="border-dusty-black-100 w-full rounded-sm border px-3 py-2"
        />
      </FormField>
    </div>
  </template>
}
